import React from "react";
import { clsx } from "clsx";
import { Link } from "react-router-dom";
import { RelatedAnalysisRowProps } from "../../../@types/rldynamicanalysis/related_analysis";
import { NoData, ThreatStatusLabel } from "../../../../../components";
import { useGetSingleRlcsReport } from "../hooks/useGetSingleRlcsReport";
import { useGetSampleFileInfo } from "../../../hooks";
import { convertGoodwareClassification } from "../RlCloudSandboxUtil";
import { Loading } from "../../subheader/components";
import { route } from "../../../../../shim/jsbridge";
import HistoryMenu from "./HistoryMenu";
import Typography from "../../../../../components/typography/Typography";
import { getNtiUrl } from "../../header/utils";

const RelatedAnalysisTableRow: React.FC<RelatedAnalysisRowProps> = ({
  analysisData,
  last
}) => {
  const relatedAnalysisType = analysisData.relationship_type.split("_").pop();
  const {
    data: cloudSandboxSingleReport,
    isFetched: cloudSandboxReportFetched
  } = useGetSingleRlcsReport(
    analysisData.sha1,
    analysisData.analysis_id,
    !!analysisData.url
  );
  const classification = cloudSandboxSingleReport?.classification;
  const [classificationLabel, classificationIcon] =
    convertGoodwareClassification(classification);
  const riskScore = cloudSandboxSingleReport?.risk_score;

  const { data: fileInfo, isFetched: fileInfoFetched } = useGetSampleFileInfo(
    analysisData.sha1
  );

  const fileType = fileInfo?.file_type ? (
    `${fileInfo?.file_type} / ${fileInfo.file_subtype}`
  ) : (
    <NoData />
  );

  const items = [
    {
      name: "classification",
      component: cloudSandboxReportFetched ? (
        <ThreatStatusLabel
          showColor={false}
          showOrb
          threatStatus={classificationIcon}
          labelText={classificationLabel}
          riskScore={riskScore}
        />
      ) : (
        <Loading />
      )
    },
    {
      name: "hash_or_url",
      component: (
        <Link
          to={
            analysisData?.url
              ? getNtiUrl(analysisData.url)
              : route("redirect_samples_details", {
                  hash_value: analysisData.sha1
                })
          }
          className="related-analysis-clickable"
          data-testid="related_analysis_sample_summary_link"
        >
          <Typography
            variant="body4"
            style={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              maxWidth: "400px"
            }}
          >
            {analysisData.url ?? analysisData.sha1}
          </Typography>
        </Link>
      )
    },
    {
      name: "type",
      component: (
        <span className="related-analysis-attachment-type">
          <Typography variant="body4">
            {relatedAnalysisType && relatedAnalysisType === "url"
              ? "URL"
              : `${relatedAnalysisType
                  ?.charAt(0)
                  .toUpperCase()}${relatedAnalysisType?.slice(1)}:`}
          </Typography>
          {relatedAnalysisType !== "url" && (
            <Typography variant="body4">
              {fileInfoFetched ? fileType : <Loading />}
            </Typography>
          )}
        </span>
      )
    },
    {
      name: "actions",
      component: (
        <HistoryMenu
          analysisId={analysisData.analysis_id}
          hash={analysisData.sha1}
          isStoredReport={false}
          isNetworkReport={!!analysisData.url}
          relatedAnalysis
          url={analysisData.url}
        />
      )
    }
  ];

  return (
    <tr>
      {items.map(({ name, component }) => (
        <td
          key={name}
          className={clsx(
            last
              ? "last-related-analysis-table-row"
              : "related-analysis-table-row",
            name === "actions" && "related-analysis-actions"
          )}
        >
          {component}
        </td>
      ))}
    </tr>
  );
};

export default RelatedAnalysisTableRow;
